import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/contexts/AuthContext";
import PhoneInput from "../../../shared/components/ui/PhoneInput";
import { getDialCodeForCountry, isValidFullPhone } from "../../../shared/utils/phoneUtils";
import { normalizeUsername } from "../../../shared/utils/usernameValidation";
import { useOnboarding } from "../OnboardingContext";
import OnboardingWizard from "../OnboardingWizard";
import { useEditStepSave } from "../useEditStepSave";

export default function StepContact() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { draft, updateDraft, goToPrevStep, completeOnboarding, isEditMode } = useOnboarding();
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!draft.countryCode) return;
    const defaultDial = getDialCodeForCountry(draft.countryCode);
    const patch = {};

    if (!draft.contact.phoneDialCode) {
      patch.phoneDialCode = defaultDial;
      patch.phoneCountryCode = draft.countryCode;
    }
    if (!draft.contact.whatsappDialCode) {
      patch.whatsappDialCode = defaultDial;
      patch.whatsappCountryCode = draft.countryCode;
    }

    if (Object.keys(patch).length > 0) {
      updateDraft({ contact: { ...draft.contact, ...patch } });
    }
  }, [draft.countryCode]);

  const handleFinish = async () => {
    if (isSaving) return;

    setSaveError(null);
    setIsSaving(true);

    const result = await completeOnboarding(user?.id ?? "me");
    setIsSaving(false);

    if (!result.ok) {
      setSaveError(result.error || "Could not save your profile. Please try again.");
      return;
    }

    document.documentElement.classList.remove("onboarding-sheet-open");
    document.body.style.overflow = "";
    navigate("/onboarding/success", { replace: true, state: location.state });
  };

  const canFinish = isValidFullPhone(draft.contact.phoneDialCode, draft.contact.phone);
  const {
    showSave,
    onSave,
    saveDisabled: quickSaveDisabled,
    saveLabel,
    saveError: quickSaveError,
  } = useEditStepSave({ saveDisabled: !canFinish });

  const updateContact = (patch) => {
    updateDraft({ contact: { ...draft.contact, ...patch } });
  };

  const errorMessage = saveError || quickSaveError;
  const usernameLabel = draft.username
    ? `@${normalizeUsername(draft.username)}`
    : "—";

  return (
    <OnboardingWizard
      hideChrome
      stepKey="contact"
      title={isEditMode ? "Contact & save" : "Contact & finish"}
      subtitle={isEditMode
        ? "Update how clients can reach you."
        : "Add a phone number so clients can reach you. WhatsApp is optional."}
      onBack={() => goToPrevStep("contact")}
      onContinue={handleFinish}
      continueLabel={isSaving ? "Saving..." : (isEditMode ? "Continue" : "Finish")}
      continueDisabled={!canFinish || isSaving}
      showSave={showSave}
      onSave={onSave}
      saveDisabled={quickSaveDisabled}
      saveLabel={saveLabel}
    >
      {errorMessage ? (
        <p className="onboarding-error-banner" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <PhoneInput
        label="Phone"
        required
        dialCode={draft.contact.phoneDialCode || getDialCodeForCountry(draft.countryCode)}
        countryCode={draft.contact.phoneCountryCode || draft.countryCode}
        value={draft.contact.phone}
        onDialPick={(option) => updateContact({
          phoneDialCode: option.dialCode,
          phoneCountryCode: option.code,
        })}
        onNumberChange={(phone) => updateContact({ phone })}
        placeholder="79 123 45 67"
      />

      <div className="onboarding-field onboarding-field--phone">
        <PhoneInput
          label="WhatsApp (optional)"
          dialCode={draft.contact.whatsappDialCode || draft.contact.phoneDialCode}
          countryCode={draft.contact.whatsappCountryCode || draft.contact.phoneCountryCode}
          value={draft.contact.whatsapp}
          onDialPick={(option) => updateContact({
            whatsappDialCode: option.dialCode,
            whatsappCountryCode: option.code,
          })}
          onNumberChange={(whatsapp) => updateContact({ whatsapp })}
          placeholder="79 123 45 67"
        />
      </div>

      <label className="onboarding-toggle">
        <span>
          <span className="onboarding-toggle__label">Webcam availability</span>
          <span className="onboarding-toggle__hint">Coming soon — save your preference now</span>
        </span>
        <input
          type="checkbox"
          checked={draft.contact.webcamEnabled}
          onChange={(e) => updateContact({ webcamEnabled: e.target.checked })}
        />
      </label>

      <div className="onboarding-review">
        <div className="onboarding-review__row">
          <span className="onboarding-review__label">Username</span>
          <span className="onboarding-review__value">{usernameLabel}</span>
        </div>
        <div className="onboarding-review__row">
          <span className="onboarding-review__label">Location</span>
          <span className="onboarding-review__value">
            {[draft.city, draft.country].filter(Boolean).join(", ") || "—"}
          </span>
        </div>
        <div className="onboarding-review__row">
          <span className="onboarding-review__label">Categories</span>
          <span className="onboarding-review__value">
            {draft.profileTypes.join(", ") || "—"}
          </span>
        </div>
        <div className="onboarding-review__row">
          <span className="onboarding-review__label">Services</span>
          <span className="onboarding-review__value">
            {draft.services.length ? `${draft.services.length} selected` : "—"}
          </span>
        </div>
        <div className="onboarding-review__row">
          <span className="onboarding-review__label">Photos</span>
          <span className="onboarding-review__value">{draft.photos.length}</span>
        </div>
      </div>
    </OnboardingWizard>
  );
}
